"use client";

import CustomSlider from "@/component/ui/CustomSlider/CustomSlider";
import React, { useState } from "react";
import ProductCard from "../Cards/ProductCard";
import UpcomingCard from "../Cards/UpcomingCard";
import { APP_URL } from "@/app/utilities/constants";
import Image from "next/image";
import {
    CarouselViewAllFooter,
    TabViewAllHeader,
    CAROUSEL_SLIDER_PAD,
} from "../CarouselViewAll";

interface TabContentItem {
   label: string;
   trackingClass?: string;
   min?: number;
   max?: number;
   viewAll?: {
      href: string;
      label?: string;
   };
}

interface Car {
   _id: string;
   name: string;
   slug: string;
   brand_name: string;
   brand_slug: string;
   featured_image?: string;
   engine_displacement: string;
   fuel_type: string;
   price_range: {
      base: number;
      top: number;
   };
   base_variant: string;
}

interface TabSectionProps {
   tabContent: TabContentItem[];
   paneContentList: any[][];
   maxItemsPerTab?: number;
   showViewAll?: boolean;
   useSlider?: boolean;
   sliderSlidesPerView?: number;
   className?: string;
   leadBtnText?: string;
   showBasePriceOnly?: boolean;
   urlPrefix?: string;
   wrapperClassName?: string;
   interlinkingpage?: "brand" | "body-type";
   trackingClass?: string;
   responsive?: {
      desktop?: number;
      tablet?: number;
      mobile?: number;
   };
   leadsumbittracking?: string;
   footerLinkHref?: string;
   footerLinkLabel?: string;
   cityName?: string;
   showCitySelectionStrip?: boolean;
   renderPaneItem?: (item: any, index: number, tabIndex: number) => React.ReactNode;
}

const TabSection: React.FC<TabSectionProps> = ({
   tabContent,
   paneContentList,
   maxItemsPerTab = 4,
   showViewAll = false,
   useSlider = false,
   sliderSlidesPerView = 4,
   responsive = {
      desktop: 4,
      tablet: 2,
      mobile: 1.2,
   },
   className = "",
   leadBtnText = "Get Best Price",
   showBasePriceOnly = true,
   wrapperClassName = "md:grid-cols-3",
   trackingClass = "",
   leadsumbittracking,
   footerLinkHref,
   footerLinkLabel,
   cityName,
   showCitySelectionStrip = false,
   renderPaneItem
}) => {
   const [activeTab, setActiveTab] = useState(0);

   const renderDefaultCard = (car: any, index: number, tabIndex: number) => {
      const isUpcomingCar = car?.status === "upcoming" ? true : false;

      if (isUpcomingCar) {
         return (
            <UpcomingCard
               car={car}
               leadBtnText={leadBtnText}
               showBasePriceOnly={showBasePriceOnly}
               trackingClass={tabContent[tabIndex].trackingClass}
               footerLinkHref={footerLinkHref}
               footerLinkLabel={footerLinkLabel}
            />
         );
      }

      return (
         <ProductCard
            car={car}
            leadBtnText={leadBtnText}
            showBasePriceOnly={showBasePriceOnly}
            index={index}
            trackingClass={tabContent[tabIndex].trackingClass}
            leadsumbittracking={leadsumbittracking}
            footerLinkHref={footerLinkHref}
            footerLinkLabel={footerLinkLabel}
         />
      );
   };

   const activeViewAll = tabContent[activeTab]?.viewAll;
   const activeViewAllHref = activeViewAll?.href ?? "";
   const activeViewAllLabel =
      activeViewAll?.label ?? `View All ${tabContent[activeTab]?.label ?? ""} Cars`;

   return (
      <div className={className}>
         {/* ---------- TABS + VIEW ALL (desktop) ---------- */}
         <div className="flex items-center gap-3 border-b border-gray-200 min-h-[42px]">
            <div className="min-w-0 flex-1 overflow-x-auto scrollbar-hide -mb-px">
               <div className="flex whitespace-nowrap">
                  {tabContent.map((tab, index) => (
                     <button
                        key={tab.label}
                        onClick={() => setActiveTab(index)}
                        className={`shrink-0 px-1 pr-5 py-2.5 font-medium outline-none focus:outline-none focus:ring-0 cursor-pointer text-[16px] md:text-[13px] lg:text-[16px] ${trackingClass} ${
                           activeTab === index
                              ? "border-b-2 border-[#008c8b] text-[#008c8b]"
                              : "text-gray-500 hover:text-black border-b-2 border-transparent"
                        }`}
                        data-testid={tab.label.toLowerCase().replace(/\s+/g, "-")}
                     >
                        {tab.label}
                     </button>
                  ))}
               </div>
            </div>

            {showViewAll && activeViewAllHref && (
               <div className="hidden md:flex shrink-0 items-center pl-3 -mb-px pb-2">
                  <TabViewAllHeader
                     href={activeViewAllHref}
                     title={activeViewAllLabel}
                  />
               </div>
            )}
         </div>

         {/* ---------- CONTENT - Render ALL tabs for SEO, show only active ---------- */}
         {paneContentList.map((tabCars, tabIndex) => {
            const tabLimitedCars = tabCars.slice(0, maxItemsPerTab);
            const tabShouldUseSlider = useSlider && tabLimitedCars.length > sliderSlidesPerView;
            const isActive = activeTab === tabIndex;

            const viewAll = tabContent[tabIndex]?.viewAll;
            const viewAllHref = viewAll?.href ?? "";
            const viewAllLabel =
               viewAll?.label ?? `View All ${tabContent[tabIndex]?.label ?? ""} Cars`;

            return (
               <div
                  key={`tab-pane-${tabIndex}`}
                  className={isActive ? "block" : "hidden"}
                  data-testid={`tab-pane-${tabIndex}`}
               >
                  {tabShouldUseSlider ? (
                     <div className={`mt-6 ${CAROUSEL_SLIDER_PAD}`}>
                        <CustomSlider
                           showArrows={true}
                           itemsToShow={sliderSlidesPerView}
                           responsive={responsive}
                        >
                           {tabLimitedCars.map((car, index) => (
                              <div key={car._id} className="">
                                 {typeof renderPaneItem === "function"
                                    ? renderPaneItem(car, index, tabIndex)
                                    : renderDefaultCard(car, index, tabIndex)}
                              </div>
                           ))}
                        </CustomSlider>
                     </div>
                  ) : (
                     <div className="mt-6">
                        <div className={`flex gap-4 overflow-x-auto md:grid md:overflow-visible scrollbar-hide -mx-[18px] px-[18px] md:mx-0 md:px-0 ${wrapperClassName}`}>
                           {tabLimitedCars.map((car, index) => (
                              <div className="flex-none w-[90%] md:w-auto" key={car._id}>
                                 {typeof renderPaneItem === "function"
                                    ? renderPaneItem(car, index, tabIndex)
                                    : renderDefaultCard(car, index, tabIndex)}
                              </div>
                           ))}
                        </div>
                     </div>
                  )}

                  {showViewAll && viewAllHref && (
                     <CarouselViewAllFooter
                        href={viewAllHref}
                        title={viewAllLabel}
                        wrapperClassName="mt-6"
                     />
                  )}

                  {showCitySelectionStrip && (
                     <div
                        className={`flex items-center gap-1.5 text-[12px] md:text-[14px] text-gray-400 font-normal ${
                           tabShouldUseSlider ? "mt-2 md:mt-0" : "mt-4"
                        }`}
                     >
                        <span>Showing Avg. Ex-Showroom Price in</span>
                        <button
                           onClick={() =>
                              window.dispatchEvent(new CustomEvent("open-location-modal"))
                           }
                           className="flex items-center gap-1 text-[#0070E0] font-semibold cursor-pointer"
                        >
                           {cityName || "Select City"}
                           <Image
                              src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}pencil.svg`}
                              alt="Edit location"
                              title="Edit location"
                              className="w-3 h-3"
                              width={10}
                              height={10}
                           />
                        </button>
                     </div>
                  )}
               </div>
            );
         })}
      </div>
   );
};

export default TabSection;